/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { type ForwardedRef, createContext, forwardRef } from 'react';
import {
  type ContextValue,
  Heading as HeadingCore,
  type HeadingProps as HeadingCoreProps,
  useContextProps,
} from 'react-aria-components';
import { $ } from '../../tokens.stylex';
import { splitByQuery } from '../../utils/split-by-query/split-by-query.util';

interface HeadingProps extends Omit<HeadingCoreProps, 'style' | 'className'> {
  /** The size variant of the heading. (Default: 16) */
  size?: keyof typeof sizeVariants;
  /** The color variant of the heading. (Default: primary) */
  color?: keyof typeof colorVariants;
  /** The weight variant of the heading. (Default: regular) */
  weight?: keyof typeof weightVariants;
  /** Highlight all occurrences of this string within children (case-insensitive). Only works when children is a plain string. */
  query?: string;
  /** Background color of the highlight mark. Matches the Message component variant. (Default: received) */
  highlightColor?: keyof typeof highlightColorVariants;
  style?: stylex.StyleXStyles;
}

const HeadingContext = createContext<ContextValue<HeadingProps, HTMLHeadingElement>>({});

const Heading = forwardRef((props: HeadingProps, ref: ForwardedRef<HTMLHeadingElement>) => {
  [props, ref] = useContextProps(props, ref, HeadingContext);

  const {
    style,
    children,
    size = 16,
    color = 'primary',
    weight = 'regular',
    query,
    highlightColor = 'yellowLight',
    ...coreProps
  } = props;

  const shouldHighlight = query && typeof children === 'string';
  const segments = shouldHighlight ? splitByQuery(children, query) : null;

  return (
    <HeadingCore
      ref={ref}
      {...stylex.props(
        styles.base,
        sizeVariants[size],
        colorVariants[color],
        weightVariants[weight],
        letterSpacingVariants[`${weight}${size.toString()}` as keyof typeof letterSpacingVariants],
        style,
      )}
      {...coreProps}>
      {segments
        ? segments.map((segment, index) => segment.isMatch
          ? <mark key={index} {...stylex.props(markStyles.base, highlightColorVariants[highlightColor])}>{segment.text}</mark>
          : segment.text)
        : children}
    </HeadingCore>
  );
});

const styles = stylex.create({
  base: {
    margin: 0,
    fontFamily: 'Inter',
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontStyle: 'normal',
  },
});

const sizeVariants = stylex.create({
  72: {
    fontSize: 72,
    lineHeight: '88px',
  },
  64: {
    fontSize: 64,
    lineHeight: '78px',
  },
  52: {
    fontSize: 52,
    lineHeight: '66px',
  },
  48: {
    fontSize: 48,
    lineHeight: '58px',
  },
  40: {
    fontSize: 40,
    lineHeight: '48px',
  },
  32: {
    fontSize: 32,
    lineHeight: '42px',
  },
  28: {
    fontSize: 28,
    lineHeight: '38px',
  },
  24: {
    fontSize: 24,
    lineHeight: '34px',
  },
  20: {
    fontSize: 20,
    lineHeight: '28px',
  },
  18: {
    fontSize: 18,
    lineHeight: '26px',
  },
  16: {
    fontSize: 16,
    lineHeight: '24px',
  },
});

const colorVariants = stylex.create({
  primary: { color: $.textPrimary },
  secondary: { color: $.textSecondary },
  tertiary: { color: $.textTertiary },
  quaternary: { color: $.textQuaternary },
  placeholder: { color: $.textPlaceholder },
  disabled: { color: $.textDisabled },
  inverted: { color: $.textInverted },
  interactive: { color: $.textInteractive },
  interactiveHover: { color: $.textInteractiveHover },
  positive: { color: $.textPositive },
  positiveHover: { color: $.textPositiveHover },
  critical: { color: $.textCritical },
  criticalHover: { color: $.textCriticalHover },
  white: { color: $.textWhite },
  black: { color: $.textBlack },
});

const weightVariants = stylex.create({
  semibold: { fontWeight: 600 },
  medium: { fontWeight: 500 },
  regular: { fontWeight: 400 },
});

const letterSpacingVariants = stylex.create({
  semibold72: { letterSpacing: -2.16 },
  medium72: { letterSpacing: -2.52 },
  regular72: { letterSpacing: -2.88 },
  semibold64: { letterSpacing: -1.6 },
  medium64: { letterSpacing: -1.92 },
  regular64: { letterSpacing: -2.24 },
  semibold52: { letterSpacing: -1.04 },
  medium52: { letterSpacing: -1.3 },
  regular52: { letterSpacing: -1.56 },
  semibold48: { letterSpacing: -0.72 },
  medium48: { letterSpacing: -0.96 },
  regular48: { letterSpacing: -1.2 },
  semibold40: { letterSpacing: -0.4 },
  medium40: { letterSpacing: -0.6 },
  regular40: { letterSpacing: -0.8 },
  semibold32: { letterSpacing: -0.16 },
  medium32: { letterSpacing: -0.32 },
  regular32: { letterSpacing: -0.48 },
  semibold28: { letterSpacing: 0 },
  medium28: { letterSpacing: 0 },
  regular28: { letterSpacing: 0 },
  semibold24: { letterSpacing: 0 },
  medium24: { letterSpacing: 0 },
  regular24: { letterSpacing: 0 },
  semibold20: { letterSpacing: 0 },
  medium20: { letterSpacing: 0 },
  regular20: { letterSpacing: 0 },
  semibold18: { letterSpacing: 0 },
  medium18: { letterSpacing: 0 },
  regular18: { letterSpacing: 0 },
  semibold16: { letterSpacing: 0 },
  medium16: { letterSpacing: 0 },
  regular16: { letterSpacing: 0 },
});

const markStyles = stylex.create({
  base: {
    borderRadius: 4,
    paddingBlock: 0,
    paddingInline: 2,
    color: 'inherit',
    display: 'inline',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    letterSpacing: 'inherit',
    lineHeight: 'inherit',
  },
});

const highlightColorVariants = stylex.create({
  yellowLight: {
    backgroundColor: $.surfaceAttentiveMedium,
  },
  yellow: {
    backgroundColor: $.surfaceAttentive,
  },
});

export { Heading, HeadingContext, type HeadingProps };
